import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { UserDTO } from "../../shared/schemas";

interface UserWithCounts extends UserDTO {
  _count: { tasks: number; events: number; habits: number };
  lastActiveAt: string | null;
  oauthAccounts: Array<{ provider: string }>;
  hasPassword: boolean;
  suspendedAt: string | null;
}

interface SignupDay {
  date: string;
  count: number;
}

interface AdminStats {
  userCount: number;
  taskCount: number;
  eventCount: number;
  habitCount: number;
  tasksByStatus: Array<{ status: string; _count: number }>;
  tasksByCategory: Array<{ category: string; _count: number }>;
  signupsPerDay: SignupDay[];
  activeUsersToday: number;
  activeUsersThisWeek: number;
  authDistribution: { email: number; google: number; github: number };
}

interface AdminSystemInfo {
  version: string;
  db: { users: number; tasks: number; events: number; habits: number; images: number; sizeBytes?: number };
  storage: { totalBytes: number; topUsers: Array<{ userId: string; name: string; email: string; bytes: number }> };
}

interface WaitlistEntry {
  id: string;
  email: string;
  createdAt: string;
}

interface ResetLinkResponse {
  resetLink: string;
}

export function useAdminUsers() {
  return useQuery<UserWithCounts[]>({
    queryKey: ["admin", "users"],
    queryFn: () => apiFetch<UserWithCounts[]>("/admin/users"),
  });
}

export function useAdminWaitlist() {
  return useQuery<WaitlistEntry[]>({
    queryKey: ["admin", "waitlist"],
    queryFn: () => apiFetch<WaitlistEntry[]>("/admin/waitlist"),
  });
}

export function useDeleteWaitlistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/waitlist/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "waitlist"] }),
  });
}

export function useAdminStats() {
  return useQuery<AdminStats>({
    queryKey: ["admin", "stats"],
    queryFn: () => apiFetch<AdminStats>("/admin/stats"),
  });
}

export function useAdminSystem() {
  return useQuery<AdminSystemInfo>({
    queryKey: ["admin", "system"],
    queryFn: () => apiFetch<AdminSystemInfo>("/admin/system"),
  });
}

export function useAdminResetLink() {
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      apiFetch<ResetLinkResponse>(`/admin/users/${userId}/reset-link`, {
        method: "POST",
      }),
  });
}

export function useAdminUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: "USER" | "ADMIN" }) =>
      apiFetch(`/admin/users/${userId}/role`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useAdminSuspendUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      apiFetch(`/admin/users/${userId}/suspend`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export type { UserWithCounts, AdminStats, AdminSystemInfo, WaitlistEntry };
